import { defineElement, html, TemplateElement } from '@webtides/element-js';
import style from './event-calendar.css';
import CalendarElementEvent from './CalendarElementEvent';
import '../dropdown/dropdown';
import DropDownEvents from '../dropdown/DropDownEvents';

class EventCalendar extends TemplateElement {
    styles() {
        return [style];
    }

    properties() {
        return {
            dataFuture: [],
            dataPast: [],
            data: {
                categories: [],
                yearOptions: [{ default: false }],
                categoryOptions: [{ default: false }],
                subCategoryOptions: [{ default: false }],
            },
            pastEvents: false,
            mobileFilter: false,
            selectedCategory: 'all',
            selectedSubCategory: 'all',
            selectedYear: 'all',
            hasDefaultCategory: false,
            filteredEvents: [],
            initialShow: 1,
            showMoreQuantity: 2,
            filteredShowQuantity: 1,
            displayShowMoreButton: true,
            subCategoryDisabled: false,
            open: false,
            editMode: false,
            modalEvent: {},
            sharedEventId: window.location.hash.replace('#', ''),
            isTeaser: false,
        };
    }

    connected() {
        super.connected();
        this.getData();
        this.filteredShowQuantity = this.initialShow;
        this.checkInitialCategory();
        this.updateSubCategories();
        this.filterEvents();
    }

    afterUpdate() {
        super.afterUpdate();
        this.openModalIfShare();
    }

    getData() {
        this.data = this.pastEvents ? this.dataPast : this.dataFuture;
    }

    parseOneEvent(event) {
        const monthNames = this.data.monthNames;

        const eventDate = new Date(event.startDate);
        const date = eventDate.getDate();
        const month = monthNames[eventDate.getMonth()];
        const year = eventDate.getFullYear();
        const localTime = eventDate.toLocaleTimeString([this.data.locale], { hour: '2-digit', minute: '2-digit' });
        const timeZone = eventDate
            .toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' })
            .substring(4);

        return { ...event, eventDate, date, month, year, localTime, timeZone };
    }

    get parsedEvents() {
        return this.data.events.map((event) => {
            return this.parseOneEvent(event);
        });
    }

    renderYear() {
        return html`
            <dropdown-element options="${JSON.stringify(this.data.yearOptions)}" type="year"></dropdown-element>
        `;
    }

    renderCategories() {
        return html`
            <dropdown-element
                options="${JSON.stringify(this.data.categoryOptions)}"
                type="category"
                disabled="${this.hasDefaultCategory}"></dropdown-element>
        `;
    }

    renderSubCategories() {
        return html`
            <dropdown-element
                options="${JSON.stringify(this.data.subCategoryOptions)}"
                type="subCategory"
                disabled="${this.subCategoryDisabled}"></dropdown-element>
        `;
    }

    addEventListeners() {
        document.addEventListener('click', this.onClick.bind(this));
    }

    onClick(e) {
        if (!(this.$refs.filterMenu.contains(e.target) || this.$refs.mobileFilter.contains(e.target))) {
            this.mobileFilter = false;
        }
    }

    filterEvents() {
        let quantityFilterApplied = false;

        this.filteredEvents = this.parsedEvents
            .filter((event) => {
                if (this.selectedYear === 'all') {
                    return true;
                }
                return this.pastEvents ? event.year <= this.selectedYear : event.year >= this.selectedYear;
            })
            .filter((event) => {
                if (this.selectedYear !== 'all') {
                    return this.selectedYear === event.year;
                }
                return true;
            })
            .filter((event) => {
                if (this.selectedCategory !== 'all') {
                    return this.selectedCategory === event.category;
                }
                return true;
            })
            .filter((event) => {
                if (this.selectedSubCategory !== 'all') {
                    return this.selectedSubCategory === event.subCategory;
                }
                return true;
            })
            .filter((event, index) => {
                const isShown = index < this.filteredShowQuantity;
                if (!quantityFilterApplied && !isShown) {
                    quantityFilterApplied = true;
                }
                return isShown;
            });
        this.displayShowMoreButton = quantityFilterApplied;
    }

    updateYears() {
        this.data.yearOptions = this.data.yearOptions.map((year) => {
            return { ...year, selected: this.selectedYear == year.value };
        });
    }

    updateCategories() {
        this.data.categoryOptions = this.data.categoryOptions.map((category) => {
            const selected = this.selectedCategory === category.value;
            return { ...category, selected, default: false };
        });
    }

    updateSubCategories() {
        if (this.selectedCategory !== 'all') {
            const subCategories = this.data.categories[this.selectedCategory]
                ? this.data.categories[this.selectedCategory]
                : [this.data.subCategoryOptions[0]];

            const subCategoryOptions = subCategories.map((subCategory) => {
                return { value: subCategory, label: subCategory, selected: subCategory === this.selectedSubCategory };
            });
            const defaultOption = [
                { ...this.data.subCategoryOptions[0], selected: this.selectedSubCategory === 'all' },
            ];
            this.data.subCategoryOptions = defaultOption.concat(subCategoryOptions);
            this.subCategoryDisabled = false;
        } else {
            this.selectedSubCategory = 'all';
            this.data.subCategoryOptions = [this.data.subCategoryOptions[0]];
            this.subCategoryDisabled = true;
        }
    }

    setInitialState() {
        this.selectedSubCategory = 'all';
        this.filteredShowQuantity = this.initialShow;
        this.updateYears();
        this.updateCategories();
        this.updateSubCategories();
    }

    checkInitialCategory() {
        const selectedCategory = this.data.categoryOptions.find((category) => category.selected);
        this.selectedCategory = selectedCategory.value;
        if (selectedCategory && selectedCategory.value === 'all') {
            this.subCategoryDisabled = true;
        }
    }

    findSelectedCategory() {
        const checkCategory = this.data.categoryOptions.find((category) => category.value === this.selectedCategory);

        if (checkCategory) {
            return checkCategory.value;
        }
        return this.data.categoryOptions[0].value;
    }

    events() {
        return {
            '[ref=comingEvents]': {
                click: () => {
                    this.pastEvents = false;
                    this.getData();
                    this.selectedYear = 'all';
                    if (this.hasDefaultCategory) {
                        this.checkInitialCategory();
                    }
                    this.selectedCategory = this.findSelectedCategory();
                    this.setInitialState();
                    this.filterEvents();
                },
            },
            '[ref=pastEvents]': {
                click: () => {
                    this.pastEvents = true;
                    this.getData();
                    this.selectedYear = 'all';
                    if (this.hasDefaultCategory) {
                        this.checkInitialCategory();
                    }
                    this.selectedCategory = this.findSelectedCategory();
                    this.setInitialState();
                    this.filterEvents();
                },
            },
            '[ref=mobileFilter]': {
                click: () => {
                    this.addEventListeners();
                    this.mobileFilter = !this.mobileFilter;
                },
            },
            '[ref="showMore"]': {
                click: () => {
                    this.filteredShowQuantity += this.showMoreQuantity;
                    this.filterEvents();
                },
            },
            this: {
                [CalendarElementEvent.CLICK]: (ev) => {
                    this.modalEvent = this.filteredEvents.find((event) => {
                        return event.id === ev.detail;
                    });
                    if (this.editMode) {
                        return;
                    }
                    if (this.modalEvent.contentLink && this.modalEvent.contentLink.href) {
                        window.open(this.modalEvent.contentLink.href, this.modalEvent.contentLink.target);
                    } else {
                        this.open();
                    }
                },
                [DropDownEvents.CHANGE_OPTION]: (ev) => {
                    if (ev.detail.type === 'year') {
                        this.selectedYear = ev.detail.value === 'all' ? 'all' : parseInt(ev.detail.value, 10);
                        this.setInitialState();
                    } else if (ev.detail.type === 'category') {
                        this.selectedCategory = ev.detail.value;
                        this.selectedSubCategory = 'all';
                        this.updateSubCategories();
                        this.requestUpdate();
                    } else if (ev.detail.type === 'subCategory') {
                        this.selectedSubCategory = ev.detail.value;
                    }
                    this.filteredShowQuantity = this.initialShow;
                    this.mobileFilter = false;
                    this.filterEvents();
                },
            },
            '[ref="eventCalendarCloseBtn"]': {
                click: () => {
                    this.close();
                },
            },
        };
    }

    open() {
        this.$refs.eventCalendarModal?.open();
    }

    close() {
        this.$refs.eventCalendarModal?.close();
    }

    openModalIfShare() {
        if (this.sharedEventId !== '' && this.$refs.eventCalendarModal) {
            const allEvents = this.dataFuture.events
                .concat(this.dataPast.events)
                .map((event) => this.parseOneEvent(event));

            this.$refs.eventCalendarModal.setAttribute('modal-id', this.sharedEventId);
            this.modalEvent = allEvents.find((event) => event.id === this.sharedEventId);
            this.open();
            this.sharedEventId = '';
            history.pushState('', document.title, window.location.pathname + window.location.search);
        }
    }

    renderModal() {
        return html`
            <modal-element
                ref="eventCalendarModal"
                disable-footer="true"
                cloak="true"
                modal-id=${this.modalEvent.id}
                modal-dialog-width="max-w-70"
                has-title-slot="false">
                <div slot="close" ref="eventCalendarCloseBtn" class="flex">
                    <svg-use name="icons-material-outline-close" class="h-6 w-6"></svg-use>
                </div>

                <div slot="content">
                    <event-calendar-element
                        modal-element="true"
                        calendar-event="${JSON.stringify(this.modalEvent)}"
                        locale="${this.data.locale}"
                        past="${this.pastEvents}"
                        edit-mode="${this.editMode}"
                        is-teaser="${this.isTeaser}"
                        more-information="${this.moreInformation}"
                        calendar-hover-text="${this.calendarHoverText}"
                        share-hover-text="${this.shareHoverText}"
                        titel="${this.titel}"
                        text="${this.text}"
                        copy-text="${this.copyText}"
                        copy-button="${this.copyButton}"
                        site="${this.data.siteUrl}"></event-calendar-element>
                </div>
            </modal-element>
        `;
    }

    template() {
        return html`
            <div>
                ${this.isTeaser
                    ? ''
                    : html`
                          <div class="relative flex flex-col justify-between sm:flex-row md:flex-col">
                              <div class="flex flex-row py-2 font-book text-grey-400">
                                  <div
                                      ref="comingEvents"
                                      class="${this.pastEvents
                                          ? 'cursor-pointer'
                                          : 'text-brand-500 border-b-2'} px-4 py-2">
                                      ${this.upcomingEventsTab}
                                  </div>
                                  <div
                                      ref="pastEvents"
                                      class="${this.pastEvents
                                          ? 'text-brand-500 border-b-2'
                                          : 'cursor-pointer'} px-4 py-2">
                                      ${this.pastEventsTab}
                                  </div>
                              </div>

                              <div
                                  ref="mobileFilter"
                                  class="${this.mobileFilter
                                      ? 'bg-grey-50'
                                      : ''} flex cursor-pointer items-center p-4 font-book text-brand-500 md:hidden">
                                  <svg-use name="icons-material-outline-filter-list" class="mr-1 h-4 w-4"></svg-use>
                                  Filter
                              </div>

                              <div
                                  ref="filterMenu"
                                  class="${this.mobileFilter
                                      ? 'absolute top-16 w-full flex flex-col bg-white shadow z-10 items-center'
                                      : 'hidden'} flex-row items-center gap-4 bg-grey-50 p-2 md:flex">
                                  ${this.renderYear()} ${this.renderCategories()} ${this.renderSubCategories()}
                              </div>
                          </div>
                      `}

                <div class="flex flex-col">
                    ${this.filteredEvents.map((event) => {
                        return html`
                            <event-calendar-element
                                calendar-event="${JSON.stringify(event)}"
                                locale="${this.data.locale}"
                                past="${this.pastEvents}"
                                edit-mode="${this.editMode}"
                                is-teaser="${this.isTeaser}"
                                more-information="${this.moreInformation}"
                                calendar-hover-text="${this.calendarHoverText}"
                                share-hover-text="${this.shareHoverText}"
                                titel="${this.titel}"
                                text="${this.text}"
                                copy-text="${this.copyText}"
                                copy-button="${this.copyButton}"
                                site="${this.data.siteUrl}"></event-calendar-element>
                        `;
                    })}
                </div>

                ${this.isTeaser
                    ? html`
                          <div class="flex flex-wrap">
                              <a
                                  href="${this.calendarPage}"
                                  target="${this.calendarPageTarget}"
                                  class="mt-8 flex-1 cursor-pointer text-center text-brand-500 hover:text-brand-600">
                                  ${this.calendarPageButtonLabel}
                              </a>
                          </div>
                      `
                    : html`
                          <div ref="showMore" class="${this.displayShowMoreButton ? '' : 'hidden'}">
                              <div class="mt-8 cursor-pointer text-center text-brand-500">${this.showMore}</div>
                          </div>
                      `}

                <div>${this.renderModal()}</div>
            </div>
        `;
    }
}

export function define() {
    defineElement('event-calendar', EventCalendar);
}
